import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/Button';
import { MapPin, Clock, Phone, Mail, ShoppingBag, Sofa, Lightbulb, Frame } from 'lucide-react';
import { Link } from 'react-router-dom';
export function StoreInfo() {
  return <Layout data-id="element-538">
      {/* Hero Section */}
      <section className="relative py-32 px-4 border-b border-taupe-200 overflow-hidden" data-id="element-539">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-540">
          <img src="https://cdn.magicpatterns.com/uploads/qvRoWqs2vhk3dGRoVgjotQ/Store_Info_bg.png" alt="Down Home Interiors Store Location" className="w-full h-full object-cover object-center" data-id="element-541" />
          <div className="absolute inset-0 bg-white/80" data-id="element-542" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-543">
          <span className="text-sage-600 uppercase tracking-[0.2em] text-xs font-semibold mb-6 block" data-id="element-544">
            Visit Us
          </span>
          <h1 className="text-4xl md:text-6xl font-serif text-charcoal-900 mb-6 leading-tight" data-id="element-545">
            Store Hours &<br data-id="element-546" />
            <span className="italic" data-id="element-547">Location</span>
          </h1>
          <p className="text-xl text-charcoal-900/70 leading-relaxed max-w-2xl mx-auto" data-id="element-548">
            Visit our showroom in Howell to explore our curated collection and
            meet with our design team.
          </p>
        </div>
      </section>

      {/* Visit Our Store */}
      <section className="py-24 px-4 bg-white" data-id="element-549">
        <div className="max-w-4xl mx-auto text-center" data-id="element-550">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-8" data-id="element-551">
            Your Destination for Inspired Living
          </h2>
          <p className="text-lg text-charcoal-900/70 leading-relaxed mb-6" data-id="element-552">
            Step into Down Home Interiors and discover a thoughtfully curated
            collection of furniture, home décor, and accessories. Our showroom
            is designed to inspire, with room vignettes that showcase how pieces
            work together to create cohesive, livable spaces.
          </p>
          <p className="text-lg text-charcoal-900/70 leading-relaxed" data-id="element-553">
            Whether you're furnishing an entire home or looking for that perfect
            finishing touch, our team is here to help you find exactly what you
            need. We also offer design consultations right in the store—bring
            your room measurements, photos, or fabric samples and let us help
            you create a plan.
          </p>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-24 px-4 bg-cream-100" data-id="element-554">
        <div className="max-w-6xl mx-auto" data-id="element-555">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-556">
            {/* Contact Information */}
            <div className="space-y-8" data-id="element-557">
              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-558">
                <div className="flex items-start gap-4 mb-6" data-id="element-559">
                  <MapPin className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-560" />
                  <div data-id="element-561">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-562">
                      Location
                    </h3>
                    <address className="not-italic text-lg text-charcoal-900/70 leading-relaxed" data-id="element-563">
                      <a href="https://www.google.com/maps/search/?api=1&query=2729+E.+Grand+River+Ave.+Howell+MI+48843" target="_blank" rel="noopener noreferrer" className="hover:text-sage-500 transition-colors" data-id="element-564">
                        2729 E. Grand River Ave.
                        <br data-id="element-565" />
                        Howell, MI 48843
                      </a>
                    </address>
                  </div>
                </div>
                <a href="https://www.google.com/maps/search/?api=1&query=2729+E.+Grand+River+Ave.+Howell+MI+48843" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-566">
                  <Button variant="outline" size="sm" data-id="element-567">
                    Get Directions
                  </Button>
                </a>
              </div>

              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-568">
                <div className="flex items-start gap-4 mb-6" data-id="element-569">
                  <Clock className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-570" />
                  <div className="flex-1" data-id="element-571">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-4" data-id="element-572">
                      Store Hours
                    </h3>
                    <div className="space-y-3" data-id="element-573">
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-574">
                        <span className="text-charcoal-900/70" data-id="element-575">Sunday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-576">
                          10:30 AM – 3:30 PM
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-577">
                        <span className="text-charcoal-900/70" data-id="element-578">Monday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-579">
                          10:00 AM – 4:00 PM
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-580">
                        <span className="text-charcoal-900/70" data-id="element-581">
                          Tuesday – Thursday
                        </span>
                        <span className="font-medium text-charcoal-900" data-id="element-582">
                          10:00 AM – 5:30 PM
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-583">
                        <span className="text-charcoal-900/70" data-id="element-584">Friday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-585">
                          10:00 AM – 5:00 PM
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-586">
                        <span className="text-charcoal-900/70" data-id="element-587">Saturday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-588">
                          Closed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-charcoal-900/60 italic" data-id="element-589">
                  Hours may vary during holidays. Call ahead to confirm.
                </p>
              </div>

              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-590">
                <div className="flex items-start gap-4" data-id="element-591">
                  <Phone className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-592" />
                  <div data-id="element-593">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-594">
                      Contact
                    </h3>
                    <div className="space-y-2 text-charcoal-900/70" data-id="element-595">
                      <p className="flex items-center gap-2" data-id="element-596">
                        <Phone size={16} data-id="element-597" />
                        <a href="tel:5175455955" className="hover:text-sage-500 transition-colors" data-id="element-598">
                          (517) 545-5955
                        </a>
                      </p>
                      <p className="flex items-center gap-2" data-id="element-599">
                        <Mail size={16} data-id="element-600" />
                        <a href="mailto:info@downhomeinteriors.com" className="hover:text-sage-500 transition-colors" data-id="element-601">
                          info@downhomeinteriors.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="h-full min-h-[600px] border border-taupe-200 shadow-card overflow-hidden" data-id="element-602">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2937.047240191924!2d-83.90287152328551!3d42.59674122011513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8823475fb0e59093%3A0x1164ffa174cf8cad!2sDown%20Home%20Interiors!5e0!3m2!1sen!2sus!4v1768330470795!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Down Home Interiors Location" data-id="element-603" />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Find In-Store */}
      <section className="py-24 px-4 bg-white" data-id="element-604">
        <div className="max-w-6xl mx-auto" data-id="element-605">
          <div className="text-center mb-16" data-id="element-606">
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-4" data-id="element-607">
              What You'll Find In-Store
            </h2>
            <p className="text-lg text-charcoal-900/60 max-w-2xl mx-auto" data-id="element-608">
              A carefully curated selection for every room in your home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-609">
            {[{
            icon: <Sofa className="text-sage-500" size={32} data-id="element-610" />,
            title: 'Furniture',
            description: 'Sofas, chairs, tables, and bedroom sets in classic and contemporary styles'
          }, {
            icon: <Lightbulb className="text-sage-500" size={32} data-id="element-611" />,
            title: 'Lighting',
            description: 'Table lamps, floor lamps, chandeliers, and decorative fixtures'
          }, {
            icon: <Frame className="text-sage-500" size={32} data-id="element-612" />,
            title: 'Décor & Art',
            description: 'Wall art, mirrors, vases, and accessories to complete your space'
          }, {
            icon: <ShoppingBag className="text-sage-500" size={32} data-id="element-613" />,
            title: 'Apparel & Gifts',
            description: 'Boutique clothing, jewelry, and unique gift items'
          }].map((category, index) => <div key={index} className="text-center" data-id="element-614">
                <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-615">
                  {category.icon}
                </div>
                <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-616">
                  {category.title}
                </h3>
                <p className="text-charcoal-900/70 leading-relaxed" data-id="element-617">
                  {category.description}
                </p>
              </div>)}
          </div>

          <div className="mt-16 bg-cream-50 p-8 md:p-12 border border-taupe-200 text-center" data-id="element-618">
            <h3 className="text-2xl font-serif text-charcoal-900 mb-4" data-id="element-619">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-lg text-charcoal-900/70 mb-6 max-w-2xl mx-auto" data-id="element-620">
              Our showroom represents just a fraction of what we can source for
              you. With access to hundreds of manufacturers and craftspeople, we
              can help you find or create exactly what you need.
            </p>
            <Link to="/contact" data-id="element-621">
              <Button data-id="element-622">Schedule a Consultation</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-taupe-200/50 to-cream-100" data-id="element-623">
        <div className="max-w-4xl mx-auto text-center" data-id="element-624">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-6" data-id="element-625">
            We'd Love to See You
          </h2>
          <p className="text-xl text-charcoal-900/70 mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-626">
            Stop by during our regular hours or contact us to schedule a private
            appointment.
          </p>
          <Link to="/contact" data-id="element-627">
            <Button size="lg" data-id="element-628">Contact Us</Button>
          </Link>
        </div>
      </section>
    </Layout>;
}